import { DateRange, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect, useRef } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { RuleModel } from '../../../../types';
import { useMutation } from '@tanstack/react-query';
import { RulesEngineFormValues } from './rules-engine-form-page';
import {
  previewImpact,
  PreviewImpactRequest,
} from '../../../api/rules-engine-api';
import {
  triggerEventOptions,
  triggerGroupUiOptions,
  getEventUiGroup,
} from './trigger-event-options';
import { actionOptions } from './action-options';
import { documentTypeOptions } from '../../../shared/google-document-types';
import { userSharingRadioOptions } from './sharing-options';
import {
  profileInfoTypeOptions,
  employeeInfoFields,
  profileFieldOptions,
} from '../../../shared/shared-profile-field-filter';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import Error from '@mui/icons-material/Error';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Search from '@mui/icons-material/Search';
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined';

const triggerGroupLabels = {
  drive: 'Drive Events',
  admin: 'Admin Events',
  calendar: 'Calendar Events',
};

const CHIP_VISIBLE_COUNT = 2;

interface ChipRowProps {
  label: string;
  items: string[];
}

function ChipRow({ label, items }: ChipRowProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, CHIP_VISIBLE_COUNT);
  const overflow = items.length - CHIP_VISIBLE_COUNT;

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          minWidth: 100,
          flexShrink: 0,
          pt: 0.25,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {visible.map((item) => (
          <Tooltip key={item} title={item} placement="top" arrow>
            <Chip
              label={item}
              size="small"
              sx={{ fontSize: '0.7rem', height: 20, maxWidth: '100%' }}
            />
          </Tooltip>
        ))}
        {!expanded && overflow > 0 && (
          <Chip
            label={`+ ${overflow}`}
            size="small"
            variant="outlined"
            onClick={() => setExpanded(true)}
            sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer' }}
          />
        )}
        {expanded && overflow > 0 && (
          <Chip
            icon={<ExpandLess sx={{ fontSize: 14 }} />}
            label=""
            size="small"
            variant="outlined"
            onClick={() => setExpanded(false)}
            sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer', px: 0 }}
          />
        )}
      </Box>
    </Stack>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  allowFullWrap?: boolean;
}

function SummaryRow({ label, value, allowFullWrap = false }: SummaryRowProps) {
  const theme = useTheme();
  const isEmpty = value === '-';
  const isMultiline = value.length > 30 || value.includes(',');

  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      alignItems={isMultiline ? 'flex-start' : 'center'}
      spacing={1}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          minWidth: 100,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Tooltip title={isEmpty ? '' : value} placement="top" arrow>
        <Typography
          variant="body2"
          sx={{
            color: isEmpty
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
            fontWeight: isEmpty ? 400 : 500,
            overflow: allowFullWrap ? 'visible' : 'hidden',
            textOverflow: allowFullWrap ? 'clip' : 'ellipsis',
            whiteSpace: isMultiline || allowFullWrap ? 'normal' : 'nowrap',
            flex: 1,
            wordBreak: isMultiline || allowFullWrap ? 'break-word' : 'normal',
            WebkitLineClamp: isMultiline && !allowFullWrap ? 2 : undefined,
            display: isMultiline && !allowFullWrap ? '-webkit-box' : undefined,
            WebkitBoxOrient:
              isMultiline && !allowFullWrap ? 'vertical' : undefined,
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    </Stack>
  );
}

interface SectionDividerProps {
  children: React.ReactNode;
}

function SectionDivider({ children }: SectionDividerProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mt: 1,
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      {children}
    </Box>
  );
}

interface RuleSummaryPanelProps {
  watch: UseFormWatch<RulesEngineFormValues>;
  getModel: () => RuleModel;
  onPreviewImpact?: (startDate: string, endDate: string) => void;
  disablePreview?: boolean;
}

export default function RuleSummaryPanel({
  watch,
  getModel,
  onPreviewImpact,
  disablePreview = false,
}: RuleSummaryPanelProps) {
  const theme = useTheme();

  const defaultEnd = dayjs();
  const defaultStart = defaultEnd.subtract(30, 'day');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    defaultStart,
    defaultEnd,
  ]);
  const dateRangeExceeds60Days =
    dateRange[0] != null &&
    dateRange[1] != null &&
    dateRange[1].diff(dateRange[0], 'day') > 60;
  const [matchCount, setMatchCount] = useState<number | undefined>(undefined);
  const [showOutdatedWarning, setShowOutdatedWarning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { mutate: runPreview, isPending: isLoading } = useMutation({
    mutationFn: ({
      payload,
      signal,
    }: {
      payload: PreviewImpactRequest;
      signal: AbortSignal;
    }) => previewImpact(payload, signal),
    onSuccess: (result) => {
      if (cancelledRef.current) return;
      setMatchCount(result.matchCount);
    },
    onError: () => {
      if (cancelledRef.current) return;
      setTimedOut(true);
    },
  });

  // Basic info
  const ruleName = watch('basicInfo.ruleName');
  const description = watch('basicInfo.description');

  // Trigger
  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');
  const occursEverytime = watch('triggerInfo.occursEverytime');
  const triggerThreshold = watch('triggerInfo.triggerThreshold');
  const hasTimeDuration = watch('triggerInfo.hasTimeDuration');
  const triggerDuration = watch('triggerInfo.triggerDuration');
  const triggerDurationType = watch('triggerInfo.triggerDurationType');
  const triggerSources = watch('triggerSources');
  const allSource = watch('allSource');
  const targetsExcluded = watch('targetsExcluded');
  const triggerCalendarSources = watch('triggerInfo.triggerCalendarSources');
  const fileMimeTypes = watch('triggerInfo.fileMimeTypes');

  // Drive filters
  const hasFileTypeFilter = watch('hasFileTypeFilter');
  const hasFileSizeFilter = watch('hasFileSizeFilter');
  const hasSharingPermissionsFilter = watch('hasSharingPermissionsFilter');
  const selectedSharingOption = watch('selectedSharingOption');

  // Actions
  const actionTodo = watch('actionInfo.actionTodo');

  // Admin filter
  const hasProfileFieldFilter = watch('hasProfileFieldFilter');
  const profileInfoType = watch('profileFieldFilter.profileInfoType');
  const profileFieldType = watch('profileFieldFilter.profileFieldType');
  const profileFieldValues = watch('profileFieldFilter.profileFieldValues');
  const profileFieldRegex = watch('profileFieldFilter.profileFieldRegex');
  const valueInputMode = watch('profileFieldFilter.valueInputMode');
  const profileFieldValueExact = watch(
    'profileFieldFilter.profileFieldValueExact'
  );
  const fieldDataType = watch('profileFieldFilter.fieldDataType');

  // Group filter
  const hasGroupNameFilter = watch('groupFilter.hasGroupNameFilter');
  const groupNameMode = watch('groupFilter.groupNameMode');
  const groupName = watch('groupFilter.groupName');
  const groupNameExact = watch('groupFilter.groupNameExact');
  const groupNameRegex = watch('groupFilter.groupNameRegex');
  const hasMemberCountFilter = watch('groupFilter.hasMemberCountFilter');
  const memberCountMode = watch('groupFilter.memberCountMode');
  const memberCountOperator = watch('groupFilter.memberCountOperator');
  const memberCount = watch('groupFilter.memberCount');
  const memberCountRangeLessThan = watch(
    'groupFilter.memberCountRangeLessThan'
  );
  const memberCountRangeGreaterThan = watch(
    'groupFilter.memberCountRangeGreaterThan'
  );

  // --- Derived display values ---

  const triggerGroupLabel = triggerEvent
    ? triggerGroupUiOptions.find(
        (o) => o.value === getEventUiGroup(triggerEvent)
      )?.displayValue ?? triggerGroup
    : triggerGroup
    ? triggerGroupLabels[triggerGroup as keyof typeof triggerGroupLabels] ??
      triggerGroup
    : '-';

  const triggerEventOption = triggerEventOptions.find(
    (opt) => opt.value === triggerEvent && opt.type === triggerGroup
  );
  const triggerActionLabel = triggerEventOption?.displayValue ?? '-';

  const occurrencesLabel = occursEverytime ? 'Every Time' : 'Threshold';

  const hasAnySources =
    (triggerSources && triggerSources.length > 0) || allSource;
  const sourceTypes = (() => {
    if (!hasAnySources) return '-';
    const types = new Set(triggerSources?.map((s) => s.type) ?? []);
    if (allSource) types.add('User');
    return Array.from(types).join(', ');
  })();

  const sourceItems: string[] =
    triggerSources
      ?.map((s) => s.value || s.selectedSharedDriveId || '')
      .filter(Boolean) ?? [];

  const excludedUserItems = targetsExcluded ?? [];

  const fileTypeItems: string[] =
    hasFileTypeFilter && fileMimeTypes && fileMimeTypes.length > 0
      ? fileMimeTypes.map(
          (mime) =>
            documentTypeOptions.find((o) => o.value === mime)?.displayValue ??
            mime
        )
      : [];

  const sharingFilterLabel = (() => {
    if (!hasSharingPermissionsFilter || !selectedSharingOption) return null;
    return (
      userSharingRadioOptions.find((o) => o.value === selectedSharingOption)
        ?.displayValue ?? selectedSharingOption
    );
  })();

  const actionItems: string[] =
    actionTodo && actionTodo.length > 0
      ? actionTodo.map(
          (a) => actionOptions.find((o) => o.value === a)?.displayValue ?? a
        )
      : [];

  const profileInfoTypeLabel =
    profileInfoTypeOptions.find((o) => o.value === profileInfoType)?.label ??
    profileInfoType ??
    '-';

  const profileFieldTypeLabel = (() => {
    if (!profileFieldType) return '-';
    const empMatch = employeeInfoFields.find(
      (f) => f.value === profileFieldType
    );
    if (empMatch) return empMatch.display;
    const orgMatch = profileFieldOptions.find(
      (f) => f.value === profileFieldType
    );
    if (orgMatch) return orgMatch.display;
    if (profileFieldType.startsWith('customAttribute.')) {
      const parts = profileFieldType.split('.');
      return parts.length >= 3
        ? `${parts[1].replace(/_/g, ' ')} - ${parts
            .slice(2)
            .join('.')
            .replace(/_/g, ' ')}`
        : profileFieldType.replace(/_/g, ' ');
    }
    return profileFieldType;
  })();

  const profileFieldValueDisplay =
    valueInputMode === 'regex'
      ? profileFieldRegex ?? '-'
      : profileFieldValues
          ?.map((v) => v.value)
          .filter(Boolean)
          .join(', ') || '-';

  const memberCountOperatorLabels: Record<string, string> = {
    equals: 'equals',
    greaterThan: 'greater than',
    lessThan: 'less than',
    greaterThanOrEqual: 'greater than or equal to',
    lessThanOrEqual: 'less than or equal to',
  };

  const isGroupEvent = getEventUiGroup(triggerEvent) === 'groupEvents';
  const effectiveMemberCountMode =
    memberCountMode === 'range' && triggerEvent !== 'UPDATE_GROUP_MEMBER'
      ? 'single'
      : memberCountMode;

  const groupNameFilterLabel =
    groupNameMode === 'regex' ? 'Regular Expression' : 'Group Name';
  const groupNameFilterValue =
    groupNameMode === 'regex'
      ? groupNameRegex ?? '-'
      : `${groupName || '-'}${groupNameExact ? ' (exact)' : ''}`;

  const memberCountFilterValue =
    effectiveMemberCountMode === 'range'
      ? `Member count is less than ${
          memberCountRangeLessThan ?? 0
        } or greater than ${memberCountRangeGreaterThan ?? 0}`
      : `${
          memberCountOperatorLabels[memberCountOperator ?? 'equals'] ?? 'equals'
        } ${memberCount ?? 0}`;

  const showGroupFilters =
    isGroupEvent &&
    ((hasGroupNameFilter && (groupName || groupNameRegex)) ||
      hasMemberCountFilter);

  const hasCalendarSources =
    allSource || (triggerCalendarSources && triggerCalendarSources.length > 0);

  const canPreview =
    !!triggerGroup &&
    !!triggerEvent &&
    (triggerGroup === 'calendar' ? hasCalendarSources : hasAnySources);

  const showDriveFilters =
    triggerGroup === 'drive' &&
    (fileTypeItems.length > 0 || hasFileSizeFilter || sharingFilterLabel);

  const showAdminFilters = triggerGroup === 'admin' && hasProfileFieldFilter;

  const showActions = actionItems.length > 0;

  const PREVIEW_MATCH_LIMITS = {
    admin: 50,
    drive: 100,
    calendar: 50,
  };

  const matchLimit =
    PREVIEW_MATCH_LIMITS[
      (triggerGroup?.toLowerCase() ?? '') as keyof typeof PREVIEW_MATCH_LIMITS
    ] ?? 50;

  // Show warning when any trigger/filter/source configuration changes after preview.
  // Excludes actionTodo (actions step) and review step — those don't affect impact.
  useEffect(() => {
    setShowOutdatedWarning((prev) => (matchCount !== undefined ? true : prev));
    setTimedOut(false);
    // intentionally omit matchCount — only config changes should fire this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange,
    triggerGroup,
    triggerEvent,
    occursEverytime,
    triggerThreshold,
    hasTimeDuration,
    triggerDuration,
    triggerDurationType,
    triggerSources,
    allSource,
    triggerCalendarSources,
    fileMimeTypes,
    hasFileTypeFilter,
    hasFileSizeFilter,
    hasSharingPermissionsFilter,
    selectedSharingOption,
    hasProfileFieldFilter,
    profileInfoType,
    profileFieldType,
    profileFieldValues,
    profileFieldRegex,
    valueInputMode,
    profileFieldValueExact,
    fieldDataType,
    hasGroupNameFilter,
    groupNameMode,
    groupName,
    groupNameExact,
    groupNameRegex,
    hasMemberCountFilter,
    memberCountMode,
    memberCountOperator,
    memberCount,
    memberCountRangeLessThan,
    memberCountRangeGreaterThan,
  ]);

  const handlePreview = () => {
    if (!canPreview || !dateRange[0] || !dateRange[1] || dateRangeExceeds60Days)
      return;
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    cancelledRef.current = false;
    setTimedOut(false);
    setShowOutdatedWarning(false);
    onPreviewImpact?.(dateRange[0].toISOString(), dateRange[1].toISOString());
    runPreview({
      payload: {
        model: getModel(),
        startDate: dateRange[0].valueOf(),
        endDate: dateRange[1].valueOf(),
      },
      signal: abortControllerRef.current.signal,
    });
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    abortControllerRef.current?.abort();
  };

  return (
    <Card
      elevation={1}
      sx={{
        width: 300,
        height: '100%',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {/* Scrollable top: Rule Summary */}
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '0 1 auto',
          minHeight: 0,
          pt: 1,
          pr: 1,
          pl: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Rule Summary
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}
        >
          Complete the form on the left to see your summary.
        </Typography>

        <Box
          sx={{
            mt: 1.5,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1,
            bgcolor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : '#EEF0F8',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
          }}
        >
          <SummaryRow label="Rule name" value={ruleName || '-'} />
          <SummaryRow label="Description" value={description} />
          <SummaryRow label="Trigger Group" value={triggerGroupLabel} />
          <SummaryRow label="Trigger Action" value={triggerActionLabel} />
          <SummaryRow label="Occurrences" value={occurrencesLabel} />
          <SummaryRow label="Source Type" value={sourceTypes} />
          <ChipRow
            label="Source(s)"
            items={allSource ? ['All users'] : sourceItems}
          />
          {allSource && excludedUserItems.length > 0 && (
            <ChipRow label="Except" items={excludedUserItems} />
          )}
          {showDriveFilters && (
            <SectionDivider>
              {fileTypeItems.length > 0 && (
                <ChipRow label="File Type Filter" items={fileTypeItems} />
              )}
              {sharingFilterLabel && (
                <SummaryRow
                  label="File Sharing Filter"
                  value={sharingFilterLabel}
                />
              )}
            </SectionDivider>
          )}

          {showAdminFilters && (
            <SectionDivider>
              <SummaryRow
                label="Profile Info Type"
                value={profileInfoTypeLabel}
              />
              <SummaryRow label="Profile Field" value={profileFieldTypeLabel} />
              <SummaryRow
                label="Field Value"
                value={profileFieldValueDisplay}
              />
            </SectionDivider>
          )}

          {showGroupFilters && (
            <SectionDivider>
              {hasGroupNameFilter && (groupName || groupNameRegex) && (
                <SummaryRow
                  label={groupNameFilterLabel}
                  value={groupNameFilterValue}
                  allowFullWrap
                />
              )}
              {hasMemberCountFilter && (
                <SummaryRow
                  label="Member Count"
                  value={memberCountFilterValue}
                  allowFullWrap
                />
              )}
            </SectionDivider>
          )}

          {showActions && (
            <SectionDivider>
              <ChipRow label="Action(s)" items={actionItems} />
            </SectionDivider>
          )}
        </Box>
      </Box>

      {/* Fixed bottom: Estimated Impact */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 1,
          pr: 1,
          pb: 1.5,
          pl: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Estimated Impact
          </Typography>
          <Tooltip
            title={
              matchCount === undefined
                ? 'Complete required fields to run estimate.'
                : 'This estimate is based on historical data from the selected date range. Actual results may vary based on future user activity.'
            }
            arrow
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: '#5B9BD5',
              }}
            />
          </Tooltip>
        </Stack>

        {showOutdatedWarning && (
          <Alert
            severity="warning"
            icon={<Error sx={{ color: '#E0982D' }} />}
            sx={{
              py: 0.5,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
              },
            }}
          >
            Estimation out of date. Update to see new impact.
          </Alert>
        )}

        <DateRangePicker
          value={dateRange}
          onChange={(newValue) => setDateRange(newValue)}
          slots={{
            field: SingleInputDateRangeField,
          }}
          slotProps={{
            textField: {
              label: 'Date Range',
              fullWidth: true,
              error: dateRangeExceeds60Days,
              helperText: dateRangeExceeds60Days
                ? 'Date range cannot exceed 60 days'
                : ' ',
            },
          }}
          maxDate={dayjs()}
          minDate={dayjs().subtract(60, 'day')}
        />

        {isLoading ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleCancel}
            startIcon={<StopCircleOutlined />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.grey[600],
              borderColor: theme.palette.grey[400],
              '&:hover': {
                borderColor: theme.palette.grey[600],
                bgcolor: 'transparent',
              },
            }}
          >
            CANCEL
          </Button>
        ) : (
          <Button
            variant="outlined"
            fullWidth
            disabled={
              disablePreview ||
              !canPreview ||
              !dateRange[0] ||
              !dateRange[1] ||
              dateRangeExceeds60Days
            }
            onClick={handlePreview}
            startIcon={<InsightsOutlined />}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            PREVIEW IMPACT
          </Button>
        )}

        <Stack alignItems="center" spacing={1.5} sx={{ flex: 1, pt: 2 }}>
          {isLoading ? (
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Skeleton variant="rounded" width="100%" height={48} />
              <Skeleton variant="rounded" width="100%" height={48} />
            </Stack>
          ) : timedOut ? (
            <>
              <InfoOutlined
                sx={{ fontSize: 24, color: theme.palette.grey[500] }}
              />
              <Typography variant="subtitle2" fontWeight={400}>
                Simulation Timeout
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  px: 1,
                }}
              >
                We couldn't fetch the simulated impact in the allotted time.
                Your rule logic is still valid.
              </Typography>
            </>
          ) : matchCount !== undefined ? (
            matchCount === 0 ? (
              <>
                <Search sx={{ fontSize: 24, color: theme.palette.grey[500] }} />
                <Typography variant="subtitle2" fontWeight={400}>
                  No activity Found
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    px: 1,
                  }}
                >
                  There were no events matching your current filters within the
                  selected date range.
                </Typography>
              </>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    fontWeight={700}
                    fontSize={20}
                    sx={{
                      color: showOutdatedWarning
                        ? theme.palette.text.disabled
                        : theme.palette.text.primary,
                    }}
                  >
                    {matchCount > matchLimit ? matchLimit : matchCount}
                  </Typography>
                  {matchCount > matchLimit && (
                    <Typography
                      fontWeight={700}
                      fontSize={20}
                      sx={{
                        color: showOutdatedWarning
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary,
                      }}
                    >
                      +
                    </Typography>
                  )}
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: showOutdatedWarning
                      ? theme.palette.text.disabled
                      : theme.palette.text.secondary,
                  }}
                >
                  Total Matches
                </Typography>
                {matchCount > matchLimit && (
                  <Alert
                    severity="info"
                    sx={{
                      py: 0.5,
                      textAlign: 'left',
                      '& .MuiAlert-message': { fontSize: '0.8rem' },
                    }}
                  >
                    {`This rule matches more than ${matchLimit} historical records. The actual impact may be much higher. We recommend refining your filters to ensure accuracy.`}
                  </Alert>
                )}
              </>
            )
          ) : (
            <>
              <InsightsOutlined
                sx={{ fontSize: 24, color: theme.palette.primary.main }}
              />
              <Typography variant="subtitle2" fontWeight={400}>
                Estimated Impact
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  px: 1,
                }}
              >
                Enter Trigger Group, Event and Trigger Source(s) to enable
                simulation.
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
}
